import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';

export interface ITrain {
  id?: number | string;
  tenant_id?: number | string;
  org_chart_node_id?: number;
  org_chart_node?: {
    id: number;
    name: string;
  };
  type?: string;
  name?: string;
  value?: string;
  description?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attachment_file?: any;
  is_published?: boolean;
  published?: boolean;
  created_at?: string;
  updated_at?: string;
  skills?: ITrainSkillResponse[];
  culture_values?: { budaya: string; deskripsi: string }[];
  knowledge_organization?: string[];
}

export interface ITrainPaginatedResponse {
  content: ITrain[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

export interface ITrainSkillItem {
  org_key_activity_id: number;
  name_of_skills: string[];
}

export interface ITrainSkillPayload {
  org_chart_node_id: number;
  skills: ITrainSkillItem[];
}

export interface ITrainSkillResponse {
  org_key_activity_id: number;
  org_key_activity?: {
    id: number;
    name: string;
  };
  name_of_skills: string[];
}

interface TrainState {
  trains: ITrain[];
  paginatedData: ITrainPaginatedResponse | null;
  selectedTrain: ITrain | null;
  isLoading: boolean;
  error: unknown;

  createTrain: (tenantId: string | number, payload: ITrainSkillPayload) => Promise<void>;
  getTrains: (
    tenantId: string | number,
    params?: {
      page?: number;
      page_size?: number;
      order?: 'asc' | 'desc';
      order_by?: string;
      org_chart_node_id?: number;
      type?: string;
      name?: string;
      published?: boolean;
    },
  ) => Promise<ITrainPaginatedResponse | void>;
  getTrainDetail: (tenantId: string | number, id: number | string) => Promise<ITrain | void>;
  updateTrain: (tenantId: string | number, id: number | string, payload: ITrainSkillPayload) => Promise<void>;
  bulkPublishTrain: (tenantId: string | number, isPublished: boolean) => Promise<void>;
  // deleteTrain removes EVERY train-trainer record for the given role
  // (org_chart_node_id) in one BE call. The matrix row represents the
  // whole role, so a row-level delete must wipe all of attitude /
  // skill / knowledge — the previous id-based call only removed the
  // single record that happened to seed the grouped response.
  deleteTrain: (tenantId: string | number, orgChartNodeId: number) => Promise<void>;
  clearTrains: () => void;
  setTrains: (trains: ITrain[]) => void;
}

const useTrainState = create<TrainState>((set, get) => ({
  trains: [],
  paginatedData: null,
  selectedTrain: null,
  isLoading: false,
  error: null,

  setTrains: trains => set({ trains }),
  clearTrains: () => set({ trains: [], paginatedData: null }),

  createTrain: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}`;
      await axiosInstance.post(url, payload);

      await get().getTrains(tenantId);

      toast.success('Pelatihan berhasil dibuat');
      set({ isLoading: false });
    } catch (error) {
      set({ error, isLoading: false });

      // The BE rejects a second create for the same role with the raw
      // string "org_chart_node_id already exists" — translate that
      // here into something the user can act on, in Indonesian, and
      // suppress the generic error dialog the default handler would
      // otherwise raise.
      const beMessage =
        (error as { response?: { data?: { error?: unknown; message?: unknown } } })?.response?.data?.error ??
        (error as { response?: { data?: { error?: unknown; message?: unknown } } })?.response?.data?.message;
      if (typeof beMessage === 'string' && beMessage === 'org_chart_node_id already exists') {
        toast.error('Peran ini sudah memiliki data Train the Trainer. Silakan edit data yang sudah ada.');
        return;
      }

      errorHandler(error);
    }
  },

  getTrains: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}`;
      const { data } = await axiosInstance.get(url, { params });

      const paginatedResponse: ITrainPaginatedResponse = {
        content: data.data?.content || [],
        totalElements: data.data?.totalElements || 0,
        totalPages: data.data?.totalPages || 0,
        size: data.data?.size || 10,
        number: data.data?.number || 0,
      };

      set({
        trains: data.data?.content || [],
        paginatedData: paginatedResponse,
        isLoading: false,
      });

      return paginatedResponse;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  getTrainDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}/${id}`;
      const { data } = await axiosInstance.get(url);
      set({ selectedTrain: data.data, isLoading: false });
      return data.data;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  updateTrain: async (tenantId, id, payload) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}/${id}`;
      const { data } = await axiosInstance.put(url, payload);
      set(state => ({
        trains: state.trains.map(t => (t.id === id ? { ...t, ...data.data } : t)),
        selectedTrain: data.data,
        isLoading: false,
      }));
      toast.success('Pelatihan berhasil diperbarui');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  deleteTrain: async (tenantId, orgChartNodeId) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}/node/${orgChartNodeId}`;
      await axiosInstance.delete(url);
      set(state => ({
        trains: state.trains.filter(t => t.org_chart_node_id !== orgChartNodeId),
        isLoading: false,
      }));
      toast.success('Pelatihan berhasil dihapus');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  bulkPublishTrain: async (tenantId: string | number, isPublished: boolean) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}/bulk-publish`;
      await axiosInstance.put(url, { published: isPublished });

      set(state => ({
        trains: state.trains.map(train => ({
          ...train,
          is_published: isPublished,
        })),
        isLoading: false,
      }));

      toast.success(
        isPublished ? 'Semua pelatihan berhasil dipublikasikan' : 'Semua pelatihan berhasil dibatalkan publikasinya',
      );
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },
}));

export default useTrainState;
